/* eslint-disable no-restricted-imports */
import { isPromise } from 'bold-ui'
import { AnyObject, Decorator, FormApi, FormState, SubmissionErrors } from 'final-form'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Form as FinalForm,
  FormProps as FinalFormProps,
  FormRenderProps as FinalFormRenderProps,
  SubmitEvent as FinalFormSubmitEvent,
  useForm,
} from 'react-final-form'
import { emptyArrayOf } from 'util/array'
import { noop } from 'util/functions'
import { get, isObjectDeepEmpty, isObjectDeepEqualById } from 'util/object'
import { isEmpty } from 'util/validation/Util'

import { PecFormApi } from '../model'
import { resetAllFieldStates, toPecFormApi } from '../util'
import { createFocusOnErrorDecorator } from './focusOnError'
import { PreserveFieldValueOnMount } from './PreserveFieldValueOnMount'
import { defaultTransformResult } from './util-form'

/**
 * O Required reflete o caso de subscription completa; com subscription parcial, os campos fora dela
 * ficam undefined em runtime apesar do tipo.
 */
export type FormRenderProps<FormValues = AnyObject> = Omit<FinalFormRenderProps<FormValues>, 'active' | 'form'> & {
  active?: string
  form: PecFormApi<FormValues>
} & Required<
    Pick<
      FormState<FormValues>,
      | 'dirty'
      | 'dirtyFields'
      | 'dirtyFieldsSinceLastSubmit'
      | 'dirtySinceLastSubmit'
      | 'hasSubmitErrors'
      | 'hasValidationErrors'
      | 'initialValues'
      | 'invalid'
      | 'modified'
      | 'modifiedSinceLastSubmit'
      | 'pristine'
      | 'submitFailed'
      | 'submitSucceeded'
      | 'submitting'
      | 'touched'
      | 'valid'
      | 'validating'
      | 'visited'
    >
  > & {
    errors: AnyObject
  }

export type ResultType = object | Promise<object | undefined> | undefined | void

interface InitialDirtyFieldsWithCustomNames<T> {
  values: T
  names?: string[]
}

export interface FormProps<FormValues> extends Omit<FinalFormProps<FormValues>, 'onSubmit' | 'render'> {
  /**
   * Para forms que usam apenas o estado dos campos, sem submeter, use o SubmitlessForm.
   */
  onSubmit(values: FormValues, form: PecFormApi<FormValues>, callback?: (errors?: SubmissionErrors) => void): ResultType
  render?: (props: FormRenderProps<FormValues>) => ReactNode
  focusOnError?: boolean
  initialDirtyFields?: FormValues | InitialDirtyFieldsWithCustomNames<FormValues>
  transformResult?(
    result: ResultType,
    supressNotificationError?: boolean,
    suppressValidationError?: boolean,
    supressFieldValidationNotificationError?: boolean
  ): ResultType
  onSubmitSucceeded?(formState: FormState<FormValues>): void
  onSubmitFailed?(formState: FormState<FormValues>, supressNotificationError?: boolean): void
  resetFormToInitialValues?: boolean
  suppressNotificationError?: boolean
  suppressValidationError?: boolean
  supressFieldValidationNotificationError?: boolean
}

const focusOnErrorDecorator = createFocusOnErrorDecorator()

/**
 * Assinatura do onSubmitFailed, espelhando a prop de mesmo nome do FormProps.
 */
export type OnSubmitFailed<FormValues = AnyObject> = (
  formState: FormState<FormValues>,
  suppressNotificationError?: boolean
) => void

const defaultOnSubmitFailedRef: { current: OnSubmitFailed } = { current: noop }

/**
 * Define o onSubmitFailed padrão da aplicação, usado por todo Form que não recebe o próprio.
 * Chamado uma vez pelo initialSetup (config/index.ts) na inicialização; até lá o padrão é um noop.
 */
export const setDefaultOnSubmitFailed = (onSubmitFailed: OnSubmitFailed) => {
  defaultOnSubmitFailedRef.current = onSubmitFailed
}

/**
 * O cast espelha o toPecFormApi: o handler padrão é agnóstico de modelo, mas o FormState do
 * final-form 5 é invariante no modelo, então o chamador informa o próprio.
 */
export function getDefaultOnSubmitFailed<FormValues = AnyObject>(): OnSubmitFailed<FormValues> {
  return (defaultOnSubmitFailedRef.current as unknown) as OnSubmitFailed<FormValues>
}

export function Form<FormValues extends object = any>(props: Readonly<FormProps<FormValues>>) {
  const {
    focusOnError = true,
    initialDirtyFields,
    transformResult = defaultTransformResult,
    onSubmitFailed = getDefaultOnSubmitFailed<FormValues>(),
    onSubmitSucceeded,
    onSubmit,
    render,
    resetFormToInitialValues = false,
    suppressNotificationError = false,
    suppressValidationError = false,
    supressFieldValidationNotificationError = true,
    ...rest
  } = props

  const resetForm = useRef(false)
  resetForm.current = resetFormToInitialValues

  const renderForm = useCallback(
    (formRenderProps: FinalFormRenderProps<FormValues>) => {
      const { form, handleSubmit } = formRenderProps

      const normalizedInitialDirtyFields: InitialDirtyFieldsWithCustomNames<FormValues> = isInitialDirtyFieldsWithCustomNames(
        initialDirtyFields
      )
        ? initialDirtyFields
        : { values: initialDirtyFields }

      if (resetForm.current) {
        setTimeout(() => resetAllFieldStates(toPecFormApi(form)))
        setTimeout(form.reset)
        resetForm.current = false
      }

      const handleSubmitWrapper = (event?: FinalFormSubmitEvent) => {
        if (onSubmitFailed && !isObjectDeepEmpty(form.getState().errors ?? {})) {
          setTimeout(() => onSubmitFailed(form.getState(), suppressNotificationError))
        }
        return handleSubmit(event)
      }

      return (
        <>
          <PreserveFieldValueOnMount />
          {normalizedInitialDirtyFields?.values && (
            <FormDirtyValuesSetter initialDirtyFields={normalizedInitialDirtyFields} />
          )}
          {render({
            ...formRenderProps,
            handleSubmit: handleSubmitWrapper,
          } as FormRenderProps<FormValues>)}
        </>
      )
    },
    [initialDirtyFields, onSubmitFailed, render, suppressNotificationError]
  )

  const formOnSubmit = useCallback(
    (values: FormValues, form: FormApi<FormValues>) => {
      const emitSubmitEvents = (submitResult: ResultType, form: FormApi<FormValues>) => {
        if (!submitResult && onSubmitSucceeded) {
          setTimeout(() => onSubmitSucceeded(form.getState()))
        }

        if (submitResult && onSubmitFailed) {
          setTimeout(() => onSubmitFailed(form.getState(), suppressNotificationError))
        }
      }

      const result = onSubmit(values, toPecFormApi(form))
      const transformed = transformResult(
        result,
        suppressNotificationError,
        suppressValidationError,
        supressFieldValidationNotificationError
      )

      if (isPromise(transformed)) {
        return transformed.then((res) => {
          emitSubmitEvents(res, form)
          return res
        })
      }

      emitSubmitEvents(transformed, form)
      return transformed
    },
    [
      onSubmit,
      onSubmitFailed,
      onSubmitSucceeded,
      suppressNotificationError,
      suppressValidationError,
      transformResult,
      supressFieldValidationNotificationError,
    ]
  )

  const externalDecorators = props.decorators ?? emptyArrayOf<Decorator<FormValues>>()
  const decorators = useMemo(() => [...externalDecorators, ...(focusOnError ? [focusOnErrorDecorator] : [])], [
    externalDecorators,
    focusOnError,
  ])

  return <FinalForm<FormValues> {...rest} onSubmit={formOnSubmit} render={renderForm} decorators={decorators} />
}

function isInitialDirtyFieldsWithCustomNames<T>(
  obj: T | InitialDirtyFieldsWithCustomNames<T>
): obj is InitialDirtyFieldsWithCustomNames<T> {
  return obj && (obj as InitialDirtyFieldsWithCustomNames<T>).values !== undefined
}

function FormDirtyValuesSetter<T>(props: { initialDirtyFields: InitialDirtyFieldsWithCustomNames<T> }) {
  const { values, names } = props.initialDirtyFields
  const [isFilled, setIsFilled] = useState(false)

  const { batch, change, getRegisteredFields, getFieldState } = useForm()

  const fieldsRegistered = !!getRegisteredFields().length
  const fieldPaths = names ?? Object.keys(values)

  useEffect(() => {
    if (values && fieldsRegistered && !isFilled) {
      batch(() => {
        fieldPaths.forEach((fieldPath) => {
          const dirtyInitialValue = get(values, fieldPath)
          const field = getFieldState(fieldPath)

          if (!field?.value) {
            if (dirtyInitialValue) change(fieldPath, dirtyInitialValue)
          } else if (!field.dirty && !areFormValuesEqual(field.initial, dirtyInitialValue)) {
            field.change(dirtyInitialValue)
            field.blur()
          }
        })
      })
      setIsFilled(true)
    }
  }, [batch, change, fieldPaths, fieldsRegistered, getFieldState, isFilled, values])

  return null
}

const normalizeEmptyValue = (value: unknown) => (!isEmpty(value) ? value : undefined)
const areFormValuesEqual = (oldValue: unknown, newValue: unknown) => {
  const a = normalizeEmptyValue(oldValue)
  const b = normalizeEmptyValue(newValue)
  return typeof b === 'object' ? isObjectDeepEqualById(a, b) : '' + a === '' + b
}
